/**
 * useTelegramLink — hook for the "Connect Telegram" deep-link flow.
 *
 * Flow:
 * 1. User calls createLink() → POST /api/me/telegram/link-token
 *    → opens the returned URL in a new tab
 *    → starts polling GET /api/me/notification-settings every 2.5s
 * 2. When telegramChatId becomes non-null → invalidates ['notification-settings'],
 *    transitions to 'connected', stops polling.
 * 3. After 2 minutes without success → transitions to 'timeout', stops polling.
 * 4. cancelPolling() stops the loop immediately and resets to 'idle'.
 * 5. reset() returns to 'idle' from 'error' or 'timeout'.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createTelegramLinkToken, getNotificationSettings } from '../api/client';

// ============================================================================
// Constants
// ============================================================================

/** How often to poll for a connected Telegram chat_id (ms). */
const POLL_INTERVAL_MS = 2_500;

/** How long to wait before giving up (ms). */
const POLL_TIMEOUT_MS = 120_000;

const NOTIFICATION_SETTINGS_KEY = ['notification-settings'] as const;

// ============================================================================
// Types
// ============================================================================

export type TelegramLinkStatus = 'idle' | 'waiting' | 'connected' | 'timeout' | 'error';

export interface TelegramLinkState {
  status: TelegramLinkStatus;
  error: string | null;
  isPolling: boolean;
  isTimedOut: boolean;
  createLink: () => Promise<void>;
  cancelPolling: () => void;
  reset: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useTelegramLink(): TelegramLinkState {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<TelegramLinkStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);

  // active flag that polling loop checks before scheduling the next tick
  const pollingActiveRef = useRef(false);
  // absolute deadline timestamp
  const deadlineRef = useRef<number>(0);
  // current scheduled poll timer
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // guard against state updates after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopPolling() {
    pollingActiveRef.current = false;
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  // Recursive polling: schedule the next poll after POLL_INTERVAL_MS.
  // Using setTimeout recursively (rather than setInterval) ensures that
  // each interval only starts after the async poll resolves, giving fake
  // timers cleaner control in tests.
  function schedulePoll() {
    if (!pollingActiveRef.current) return;

    pollTimerRef.current = setTimeout(() => {
      if (!pollingActiveRef.current || !mountedRef.current) return;

      // Check timeout deadline
      if (Date.now() >= deadlineRef.current) {
        stopPolling();
        if (!mountedRef.current) return;
        setIsPolling(false);
        setIsTimedOut(true);
        setStatus('timeout');
        return;
      }

      // Run the actual poll; schedule next tick regardless of success/failure
      getNotificationSettings()
        .then((settings) => {
          if (!pollingActiveRef.current || !mountedRef.current) return;

          if (settings.telegramChatId !== null) {
            stopPolling();
            setIsPolling(false);
            setStatus('connected');
            void queryClient.invalidateQueries({ queryKey: NOTIFICATION_SETTINGS_KEY });
          } else {
            // Not yet connected — schedule the next poll
            schedulePoll();
          }
        })
        .catch(() => {
          if (!pollingActiveRef.current || !mountedRef.current) return;
          // Transient network error — keep polling
          schedulePoll();
        });
    }, POLL_INTERVAL_MS);
  }

  function startPolling() {
    if (!mountedRef.current) return;
    deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollingActiveRef.current = true;
    setIsPolling(true);
    schedulePoll();
  }

  const createLink = useCallback(async () => {
    setError(null);
    setStatus('idle');

    try {
      const linkToken = await createTelegramLinkToken();

      // Only open the window if the call succeeded
      window.open(linkToken.url, '_blank', 'noopener');

      if (!mountedRef.current) return;
      setStatus('waiting');
      startPolling();
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message =
        err instanceof Error ? err.message : 'Failed to create Telegram link. Please try again.';
      setError(message);
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelPolling = useCallback(() => {
    stopPolling();
    if (!mountedRef.current) return;
    setIsPolling(false);
    setIsTimedOut(false);
    setStatus('idle');
    setError(null);
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    if (!mountedRef.current) return;
    setIsPolling(false);
    setIsTimedOut(false);
    setStatus('idle');
    setError(null);
  }, []);

  return {
    status,
    error,
    isPolling,
    isTimedOut,
    createLink,
    cancelPolling,
    reset,
  };
}
